import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Text, Radio, RadioGroup, Checkbox,
} from 'informed';
import moment from 'moment';

import DateText from '../../DateText.jsx';
import { Label, Error } from '../../Typography.jsx';
import { Button, PrimaryButton } from '../../Buttons.jsx';

import styles from '../../../styles/patientPages/informationHistory/GeneralInfo.scss';

import {
  updateGeneralInfo,
} from '../../../actions/informationHistoryActions';

type Props = {
  activePatientId: string,
  generalInfo: Object,
  mostRecentHeight: number,
  mostRecentWeight: number,
  houseHistory: Object[],
  allergies: Object[],
  formApi: Object,
  updateGeneralInfo: Function,
  print: boolean,
}

type State = {
  editing: bool,
}

class GeneralInfoComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      editing: false,
    };
  }

  getAge(dobString: string) {
    const dob = moment(dobString);
    const now = moment();
    const years = now.diff(dob, 'years');
    const totalMonths = now.diff(dob, 'months');
    dob.add(years, 'years');
    const months = now.diff(dob, 'months');
    dob.add(months, 'months');
    const days = now.diff(dob, 'days');
    return `${years} y, ${months} m, ${days} d (${totalMonths} m ${days} d)`;
  }

  onCancel = () => {
    this.setState({
      editing: false,
    });
  }

  onSave = () => {
    const { invalid, values } = this.props.formApi.getState();
    if (invalid) {
      return;
    }
    values.dob = moment(values.dob, 'DD-MM-YYYY').toISOString();
    values.dateArrived = moment(values.dateArrived, 'DD-MM-YYYY').toISOString();
    values.dateDeceased = moment(values.dateDeceased, 'DD-MM-YYYY').toISOString();
    this.props.updateGeneralInfo(this.props.activePatientId, values);
    this.setState({
      editing: false,
    });
  }

  onEdit = () => {
    this.setState({
      editing: true,
    });
  }

  render() {
    const {
      firstName, siswatiName, surname, sex, dob, dateArrived, dateDeceased, archived,
    } = this.props.generalInfo;

    let firstNameContent = firstName;
    let siswatiNameContent = siswatiName;
    let surnameContent = surname;
    let sexContent = sex;
    let dobContent = dob ? moment(dob).format() : dob;
    let dateArrivedContent = dateArrived ? moment(dateArrived).format() : dateArrived;
    const ageContent = dob ? this.getAge(dob) : dob;
    let dateDeceasedContent = dateDeceased ? moment(dateDeceased).format() : dateDeceased;
    let archivedRow = archived && <tr><td><Error>ARCHIVED</Error></td></tr>;

    const allergyContent = this.props.allergies.map(allergy => allergy.allergen).join(', ');

    if (this.state.editing) {
      firstNameContent = <Text field='firstName' initialValue={firstName} />;
      siswatiNameContent = <Text field='siswatiName' initialValue={siswatiName} />;
      surnameContent = <Text field='surname' initialValue={surname} />;
      sexContent = <RadioGroup field='sex' initialValue={sex}>
        M: <Radio value='M' />
        F: <Radio value='F' />
      </RadioGroup>;
      dobContent = <DateText field='dob' item={{ dob }} />;
      dateArrivedContent = <DateText field='dateArrived' item={{ dateArrived }} initEmpty />;
      dateDeceasedContent = <DateText field='dateDeceased' item={{ dateDeceased }} initEmpty />;
      archivedRow = <tr>
        <td><Label>Archived: </Label></td>
        <td><Checkbox field='archived' initialValue={archived} /></td>
      </tr>;
    }

    return (
      <div>
        <div className={styles.contentContainer}>
          <table className={styles.condensedTable}>
            <tbody>
              <tr>
                <td><Label>First Name:</Label></td>
                <td>{firstNameContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Siswati Name:</Label></td>
                <td>{siswatiNameContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Surname:</Label></td>
                <td>{surnameContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Sex:</Label></td>
                <td>{sexContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Date of Birth:</Label></td>
                <td>{dobContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Age:</Label></td>
                <td>{ageContent || '--'}</td>
              </tr>
            </tbody>
          </table>

          <table className={styles.condensedTable}>
            <tbody>
              <tr>
                <td><Label>Height:</Label></td>
                <td>{`${this.props.mostRecentHeight} cm`}</td>
              </tr>
              <tr>
                <td><Label>Weight:</Label></td>
                <td>{`${this.props.mostRecentWeight} kg`}</td>
              </tr>
              <tr>
                <td><Label>Allergies:</Label></td>
                <td>{allergyContent || 'NKA'}</td>
              </tr>
              <tr>
                <td><Label>House:</Label></td>
                <td>{this.props.houseHistory[0] ? this.props.houseHistory[0].house || '--' : '--'}</td>
              </tr>
              <tr>
                <td><Label>Date arrived at PC:</Label></td>
                <td>{dateArrivedContent || '--'}</td>
              </tr>
              <tr>
                <td><Label>Date Deceased:</Label></td>
                <td>{dateDeceasedContent || '--'}</td>
              </tr>
              {archivedRow}
            </tbody>
          </table>
        </div>

        {!this.props.print
          && (this.state.editing
            ? (
              <div>
                <PrimaryButton onClick={this.onSave}>Save</PrimaryButton>
                <Button onClick={this.onCancel}>Cancel</Button>
              </div>
            ) : (
              <PrimaryButton onClick={this.onEdit}>Edit</PrimaryButton>
            )
          )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  generalInfo: state.patient.data.generalInfo,
  mostRecentHeight: state.patient.data.mostRecentHeight,
  mostRecentWeight: state.patient.data.mostRecentWeight,
  houseHistory: state.patient.data.houseHistory,
  allergies: state.patient.data.allergies,
  print: state.patient.print,
});

const mapDispatchToProps = dispatch => ({
  updateGeneralInfo:
    (patientId, values) => dispatch(updateGeneralInfo(patientId, values)),
});

const GeneralInfo = connect(
  mapStateToProps,
  mapDispatchToProps,
)(GeneralInfoComponent);

export default GeneralInfo;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/informationHistory/GeneralInfo.jsx